import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { RulerIcon } from "./viewer-icons";
import {
  createMetricsJob,
  getMetricsProgress,
} from "../../services/remote-api";

const PROGRESS_POLL_MS = 2000;
const PROGRESS_TIMEOUT_MS = 10 * 60 * 1000; // give up polling after 10 minutes

// shown when the job ended without results ("Failed" / "Landmarks needed").
const FAILED_MESSAGE =
  "Oops! We could not measure your scan. Please follow the tutorial video and rescan.";

// the measurements shown to the user, in display order: the `key` of the
// entry in the results JSON, and its label.
const SHOWN_MEASUREMENTS = [
  { key: "footLen", label: "Length" },
  { key: "footWid", label: "Width" },
  { key: "instepH", label: "Instep height" },
  { key: "F", label: "Instep girth" },
  { key: "G", label: "Ball girth" },
];

// metrics job states that stop the polling. "Incomplete scan" means the model
// was cleaned but the scan does not cover enough of the foot for all of its
// measurements: it comes with the same result files as "Succeed", and only its
// cleaned mesh is shown. "Landmarks needed" means the job could not find the
// landmarks by itself: it is handled as "Failed", the scan is to be taken again.
const TERMINAL_STATUSES = [
  "Succeed",
  "Incomplete scan",
  "Failed",
  "Landmarks needed",
];

// the measurements shown of one measured foot:
// [{ key, label, value, box, a, b, plane, seed }]. What is there to draw the
// dimension line depends on the kind of measurement: `box` is the measured
// bounding box (the foot length and width), `a` and `b` are the two end points
// ([x, y, z]) of a measured span (the instep height), `plane` is the cut plane
// ({ normal, point }) of a girth and `seed` a point on its contour.
function shownMeasurements(measurements) {
  return SHOWN_MEASUREMENTS.map(({ key, label }) => {
    const found = (measurements || []).find((m) => m && m.key === key);
    return {
      key,
      label,
      value: found && found.value,
      box: found && found.box,
      a: found && found.a,
      b: found && found.b,
      plane: found && found.plane,
      seed: found && found.seed,
    };
  });
}

// the contours of the problems found in the scan (holes: results.quality.holes,
// spikes: results.quality.spikes), one flat array of numbers per hole / spike:
// every 6 numbers are a line segment (x1, y1, z1, x2, y2, z2), in no
// particular order.
function problemContours(problems) {
  return (Array.isArray(problems) ? problems : [])
    .map((problem) => {
      const segments = (problem && problem.segments) || [];
      // drop a trailing partial segment
      return segments.slice(0, segments.length - (segments.length % 6));
    })
    .filter((segments) => segments.length);
}

// download and parse the measurement results JSON of a finished job.
// Returns { sides, holes }:
// `sides` are the measured feet, one or two (none for an incomplete scan):
// [{ side, measurements }], `side` being which foot ("left" / "right") and
// `measurements` its shown measurements (see shownMeasurements());
// `holes` are the contours of the holes and of the spikes (dents) found in the
// scan (see problemContours()), drawn alike, will be shown if the scan is
// incomplete.
async function fetchMeasurements(jsonUrl) {
  const response = await fetch(jsonUrl);
  if (!response.ok) {
    throw new Error(`(${response.status}) Failed to download the results.`);
  }
  const results = await response.json();
  return {
    sides: (results.sides || []).map((foot) => ({
      side: foot.side || null,
      measurements: shownMeasurements(foot.measurements),
    })),
    holes: [
      ...problemContours(results.quality && results.quality.holes),
      ...problemContours(results.quality && results.quality.spikes),
    ],
  };
}

// whether any of the measured feet has a value to show.
function anyMeasured(sides) {
  return sides.some((foot) =>
    foot.measurements.some((m) => typeof m.value === "number"),
  );
}

// the metrics job parameters:
// {
//    relatedLabId,
//    scanType,
//    webOrderId,
//    webOrderUserName,
//    dataUrl,
//    dataFilename
// },
// where dataUrl/dataFilename are from the vision job that produced the scan.
const inputPropType = PropTypes.shape({
  relatedLabId: PropTypes.string,
  scanType: PropTypes.string,
  webOrderId: PropTypes.string,
  webOrderUserName: PropTypes.string,
  dataUrl: PropTypes.string,
  dataFilename: PropTypes.string,
});

// Runs the server-side automatic size measurement on the scanned model:
// creates the metrics job for `input` and tracks it to its end.
//
// auto: start the measurement as soon as the component mounts (once — a
// StrictMode remount does not start a second job).
//
// onMeasured: called with
// {
//    jobId,
//    dataUrl,
//    modelUrl,
//    modelUrlAt,
//    complete,
//    sides,
//    holes,
// }
// once the job succeeds, for the viewer to display measuring results and the
// caller to save them. `sides` are the measured feet (see
// fetchMeasurements()). `complete` is false when the job ended as
// "Incomplete scan": the mesh is there, but the measurements are not to be
// shown — the contours of its `holes` are, instead.
//
// Returns { state, status, error, running, measure, cancel }:
// state: null (idle) | "starting" | "measuring" | "loading" | "succeeded" |
//   "finished" (the job ended without results, e.g. failed) | "error"
// status: the latest GET /api/metrics response of the running job
// measure(): (re)start the measurement; cancel(): abandon the running job
//   (its results are kept on the server) and go idle.
function useMeasurement({ input, onMeasured, auto = false }) {
  const [state, setState] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const pollRef = useRef(null);
  const pollDeadlineRef = useRef(0);
  // measurement generation — bumped on every new run (and on cancel) so that
  // responses and poll timers of a superseded job are ignored.
  const runSeqRef = useRef(0);
  // whether the component is mounted: responses arriving after it unmounts
  // are dropped. (A ref rather than a seq bump, so that StrictMode's
  // simulated unmount/remount in development does not orphan the auto-run.)
  const activeRef = useRef(false);
  const autoStartedRef = useRef(false);

  useEffect(() => {
    activeRef.current = true;
    if (auto && !autoStartedRef.current) {
      autoStartedRef.current = true;
      measure();
    }
    return () => {
      activeRef.current = false;
      if (pollRef.current) {
        clearTimeout(pollRef.current);
        pollRef.current = null;
      }
    };
    // the auto-run is a mount-time decision only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // whether the run `seq` was superseded or the component is gone.
  function superseded(seq) {
    return seq !== runSeqRef.current || !activeRef.current;
  }

  function stopPolling() {
    runSeqRef.current += 1; // invalidate any in-flight request/timer
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }

  // create the metrics job for the viewed model, then track its progress.
  async function measure() {
    stopPolling();
    const seq = runSeqRef.current;
    setError(null);
    setStatus(null);
    setState("starting");
    try {
      const job = await createMetricsJob(input);
      if (superseded(seq)) return;
      setState("measuring");
      pollDeadlineRef.current = Date.now() + PROGRESS_TIMEOUT_MS;
      pollProgress(job, seq);
    } catch (err) {
      if (superseded(seq)) return;
      setError(err.message);
      setState("error");
    }
  }

  // poll the metrics job ({ jobId, dataUrl }) until it reaches a terminal
  // state, or times out.
  async function pollProgress(job, seq) {
    const { jobId } = job;
    if (superseded(seq)) return;
    try {
      const next = await getMetricsProgress(jobId);
      if (superseded(seq)) return;
      setStatus(next);

      const complete = next.jobStatus === "Succeed";
      if (complete || next.jobStatus === "Incomplete scan") {
        // two result files: the cleaned mesh (STL) first, the measurement
        // results JSON second. A job that reports success without them has
        // nothing to show, so it is reported as a failure instead, which the
        // user can retry.
        const urls = next.presignedUrls || [];
        if (!urls[0] || !urls[1]) {
          throw new Error(
            "Oops! Unable to load your results. Please try again.",
          );
        }
        setState("loading");
        const { sides, holes } = await fetchMeasurements(urls[1]);
        if (superseded(seq)) return;
        // throw if the job ended as "Succeed" but the results are empty
        if (complete && !anyMeasured(sides)) {
          throw new Error(
            "Oops! Unable to load your results. Please try again.",
          );
        }
        setState("succeeded");
        // hand the results to the viewer, which keeps showing them.
        if (onMeasured) {
          onMeasured({
            jobId,
            // the remote S3 folder which contains the job result files
            dataUrl: next.dataUrl || job.dataUrl,
            modelUrl: urls[0],
            // the mesh URL expires ~10 minutes from now
            modelUrlAt: Date.now(),
            complete,
            sides,
            holes,
          });
        }
      } else if (TERMINAL_STATUSES.includes(next.jobStatus)) {
        setState("finished");
      } else if (Date.now() >= pollDeadlineRef.current) {
        setError(
          "The measurement is taking longer than expected. Please try again later.",
        );
        setState("error");
      } else {
        // pending / in progress — check again shortly.
        pollRef.current = setTimeout(
          () => pollProgress(job, seq),
          PROGRESS_POLL_MS,
        );
      }
    } catch (err) {
      if (superseded(seq)) return;
      setError(err.message);
      setState("error");
    }
  }

  // abandon the running job (its results are kept on the server) and go idle.
  function cancel() {
    stopPolling();
    setState(null);
    setStatus(null);
    setError(null);
  }

  const running =
    state === "starting" || state === "measuring" || state === "loading";

  return { state, status, error, running, measure, cancel };
}

// Floating round button running the size measurement on the scanned model on
// request (body groups with useMetrics "enabled"), and the dialog showing the
// measurement progress.
// input / onMeasured: see useMeasurement().
export const MeasureButton = ({ input, onMeasured, onRescan }) => {
  const theme = useTheme();
  const green = theme.palette.primary.main;
  const [open, setOpen] = useState(false);
  const { state, status, error, running, measure, cancel } = useMeasurement({
    input,
    onMeasured,
  });

  // the measurement takes a while and runs on the server — confirm first.
  function handleOpen() {
    cancel();
    setOpen(true);
  }

  // closing abandons the running job (its results are kept on the server).
  function handleClose() {
    cancel();
    setOpen(false);
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="Auto measure"
        onClick={handleOpen}
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          p: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
          border: `2px solid ${green}`,
          boxShadow: 3,
        }}
      >
        <RulerIcon size={30} color={green} />
      </Box>

      <MeasureDialog
        open={open}
        state={state}
        status={status}
        error={error}
        running={running}
        onMeasure={measure}
        onClose={handleClose}
        onRescan={onRescan}
      />
    </>
  );
};

MeasureButton.propTypes = {
  onMeasured: PropTypes.func,
  onRescan: PropTypes.func,
  input: inputPropType.isRequired,
};

// Automatic size measurement that starts by itself as soon as the scanned
// model is previewed (body groups with useMetrics "auto"): its progress is
// shown in a card over the model, which the user can keep looking at meanwhile.
// When the job does not end with results, the same dialog as the button's
// reports it, with a retry.
// input / onMeasured: see useMeasurement().
// onRescan: discard the scan and take it again, offered when the job failed.
// onRunning: called with whether the measurement is running right now.
export const AutoMeasureStatus = ({
  input,
  onMeasured,
  onRunning,
  onRescan,
}) => {
  const { state, status, error, running, measure, cancel } = useMeasurement({
    input,
    onMeasured,
    auto: true,
  });

  // the latest callback, so that the effect below only follows `running`.
  const onRunningRef = useRef(onRunning);
  onRunningRef.current = onRunning;
  useEffect(() => {
    if (onRunningRef.current) onRunningRef.current(running);
    return () => {
      if (onRunningRef.current) onRunningRef.current(false);
    };
  }, [running]);

  return (
    <>
      {running && (
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: 3,
            px: 2,
            py: 1.5,
            boxShadow: 3,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Auto Measuring
          </Typography>
          <MeasureProgress state={state} status={status} />
        </Box>
      )}

      <MeasureDialog
        open={state === "error" || state === "finished"}
        state={state}
        status={status}
        error={error}
        running={running}
        onMeasure={measure}
        onClose={cancel}
        onRescan={onRescan}
      />
    </>
  );
};

AutoMeasureStatus.propTypes = {
  onMeasured: PropTypes.func,
  onRunning: PropTypes.func,
  onRescan: PropTypes.func,
  input: inputPropType.isRequired,
};

// the measurement dialog: the confirmation before it starts (state null),
// its progress while it runs, and its error with a retry.
const MeasureDialog = ({
  open,
  state,
  status,
  error,
  running,
  onMeasure,
  onClose,
  onRescan,
}) => {
  // the job ended without results: the dialog is not dismissed by tapping
  // outside it, only by its buttons.
  const failed = state === "finished";

  return (
    <Dialog
      open={open}
      onClose={() => !running && !failed && onClose()}
      fullWidth
      maxWidth="xs"
      sx={{ zIndex: 1500 }}
    >
      <DialogTitle>Auto Measuring</DialogTitle>
      <DialogContent>
        {state === null ? (
          <Typography>Measure the size of this scan automatically?</Typography>
        ) : state === "error" ? (
          <Alert severity="error">{error}</Alert>
        ) : state === "finished" ? (
          <Alert severity="error">{FAILED_MESSAGE}</Alert>
        ) : (
          <MeasureProgress state={state} status={status} />
        )}
      </DialogContent>
      <DialogActions>
        {state === null && <Button onClick={onClose}>Cancel</Button>}
        {state === null && (
          <Button onClick={onMeasure} sx={{ fontWeight: 700 }}>
            Measure
          </Button>
        )}
        {/* after an error: give up, or run the whole measurement again (not
          just the step that failed). */}
        {state === "error" && <Button onClick={onClose}>Cancel</Button>}
        {state === "error" && (
          <Button onClick={onMeasure} sx={{ fontWeight: 700 }}>
            Measure Again
          </Button>
        )}
        {state !== null && state !== "error" && (
          <Button onClick={onClose} disabled={running}>
            Close
          </Button>
        )}
        {/* the job ended without results: the scan is to be taken again. */}
        {failed && onRescan && (
          <Button color="error" onClick={onRescan} sx={{ fontWeight: 700 }}>
            Discard &amp; Rescan
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

MeasureDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  state: PropTypes.string,
  status: PropTypes.object,
  error: PropTypes.string,
  running: PropTypes.bool.isRequired,
  onMeasure: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRescan: PropTypes.func,
};

// the metrics job's own status label and progress.
const MeasureProgress = ({ state, status }) => {
  const label =
    state === "starting"
      ? "Starting the measurement…"
      : state === "loading"
        ? "Loading the results…"
        : status && status.jobStatusLabel
          ? status.jobStatusLabel
          : "Measuring the scan…";
  const progress =
    status && typeof status.progress === "number" ? status.progress : 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {state !== "finished" && state !== "succeeded" && (
          <CircularProgress size={20} />
        )}
        <Typography>{label}</Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, progress))}
          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
        />
        <Typography variant="body2" color="text.secondary">
          {Math.round(progress)}%
        </Typography>
      </Box>
    </Box>
  );
};

MeasureProgress.propTypes = {
  state: PropTypes.string,
  status: PropTypes.object,
};
